import React, { useEffect, useState } from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import EndCallButton from '../Buttons/EndCallButton/EndCallButton';
import { isMobile } from '../../utils';
import Menu from './Menu/Menu';
import useParticipants from '../../hooks/useParticipants/useParticipants';
import useRoomState from '../../hooks/useRoomState/useRoomState';
import useVideoContext from '../../hooks/useVideoContext/useVideoContext';
import { Typography, Grid, Hidden, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@material-ui/core';
import ToggleAudioButton from '../Buttons/ToggleAudioButton/ToggleAudioButton';
import ToggleChatButton from '../Buttons/ToggleChatButton/ToggleChatButton';
import ToggleVideoButton from '../Buttons/ToggleVideoButton/ToggleVideoButton';
import ToggleScreenShareButton from '../Buttons/ToogleScreenShareButton/ToggleScreenShareButton';
import ToggleCaptionsButton from '../Buttons/ToggleCaptionsButton/ToggleCaptionsButton';
import defaultconfig, { VIDEO_API_BASE } from '../../UrlFiles/UrlFiles';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      backgroundColor: theme.palette.background.default,
      bottom: 0,
      left: 0,
      right: 0,
      height: `${theme.footerHeight}px`,
      position: 'fixed',
      display: 'flex',
      padding: '0 1.43em',
      zIndex: 10,
      [theme.breakpoints.down('sm')]: {
        height: `${theme.mobileFooterHeight}px`,
        padding: 0,
      },
    },
    screenShareBanner: {
      position: 'fixed',
      zIndex: 8,
      bottom: `${theme.footerHeight}px`,
      left: 0,
      right: 0,
      height: '104px',
      background: 'rgba(0, 0, 0, 0.5)',
      '& h6': {
        color: 'white',
      },
      '& button': {
        background: 'white',
        color: theme.brand,
        border: `2px solid ${theme.brand}`,
        margin: '0 2em',
        '&:hover': {
          color: '#600101',
          border: `2px solid #600101`,
          background: '#FFE9E7',
        },
      },
    },
    hideMobile: {
      display: 'initial',
      [theme.breakpoints.down('sm')]: {
        display: 'none',
      },
    },
    warningBanner: {
      position: 'fixed',
      zIndex: 9,
      top: 0,
      left: 0,
      right: 0,
      background: '#d32f2f',
      color: 'white',
      padding: '8px 16px',
      textAlign: 'center',
      fontSize: '14px',
    },
  })
);

export default function MenuBar() {
  const classes = useStyles();
  const { isSharingScreen, toggleScreenShare } = useVideoContext();
  const roomState = useRoomState();
  const isReconnecting = roomState === 'reconnecting';
  const { room } = useVideoContext();
  const participants = useParticipants();

  const params = new URLSearchParams(window.location.search);
  const role = params.get('role');
  const joinToken = params.get('join_token');
  const isNewFlow = !!role && !!joinToken;
  const isRecruiter = role === 'recruiter';

  const roomName = room?.name || window.location.pathname.split('/room/')[1];

  const [minutesRemaining, setMinutesRemaining] = useState<number | null>(null);
  const [showWarning, setShowWarning] = useState(false);
  const [showExtendDialog, setShowExtendDialog] = useState(false);
  const [creditsExhausted, setCreditsExhausted] = useState(false);
  const [exhaustedCountdown, setExhaustedCountdown] = useState(60);
  const [creditsWarning, setCreditsWarning] = useState(false);

  useEffect(() => {
  if (!isNewFlow || !roomName) return;

  let isMounted = true;

  const poll = setInterval(async () => {
    try {
      const res = await fetch(`${VIDEO_API_BASE}/video/session-status/${roomName}?join_token=${joinToken}`);
      const data = await res.json();

      // console.log('[MenuBar poll] sessionStatus response:', data);

      if (!isMounted) return;

      setMinutesRemaining(data.minutes_remaining);
      setCreditsWarning(data.credits_warning);

      if (data.credits_exhausted && !creditsExhausted) {
        setCreditsExhausted(true);
        setExhaustedCountdown(60);
      }

      if (data.warning && !showExtendDialog) {
        setShowWarning(true);
        if (isRecruiter) setShowExtendDialog(true);
      }

      if (data.minutes_remaining !== null && data.minutes_remaining === 0) {
        // console.log('[MenuBar poll] minutes_remaining is 0, disconnecting');
        room?.disconnect();
        clearInterval(poll);
      }
    } catch (e) {
      // console.error('[MenuBar poll] error:', e);
    }
  }, 30000);

  return () => {
    isMounted = false;
    clearInterval(poll);
  };
}, [isNewFlow, roomName, room]);
  useEffect(() => {
    if (!creditsExhausted) return;
    if (exhaustedCountdown <= 0) {
      room?.disconnect();
      return;
    }
    const t = setTimeout(() => setExhaustedCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [creditsExhausted, exhaustedCountdown, room]);

  const handleExtend = async () => {
    if (!joinToken) return;
    try {
      await fetch(`${VIDEO_API_BASE}/video/extend-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ join_token: joinToken }),
      });
      setShowWarning(false);
      setShowExtendDialog(false);
    } catch (e) {
      // console.error('extend-session failed', e);
    }
  };

  return (
    <>
      {creditsExhausted && (
        <div className={classes.warningBanner}>
          Credits exhausted — interview ending in {exhaustedCountdown}s
        </div>
      )}

      {creditsWarning && !creditsExhausted && (
        <div className={classes.warningBanner} style={{ background: '#f57c00' }}>
          Credit balance is low
        </div>
      )}

      {isSharingScreen && (
        <Grid container justifyContent="center" alignItems="center" className={classes.screenShareBanner}>
          <Typography variant="h6">You are sharing your screen</Typography>
          <Button onClick={() => toggleScreenShare()}>Stop Sharing</Button>
        </Grid>
      )}

      <footer className={classes.container}>
        <Grid container justifyContent="space-around" alignItems="center">
          <Hidden smDown>
            <Grid style={{ flex: 1 }}>
              <Typography variant="body1">
                {room!.name} | {participants.length + 1} participant{participants.length ? 's' : ''}
                {minutesRemaining !== null && isNewFlow && (
                  <span style={{ marginLeft: 12, color: minutesRemaining <= 2 ? '#d32f2f' : 'inherit' }}>
                    · {minutesRemaining}m remaining
                  </span>
                )}
              </Typography>
            </Grid>
          </Hidden>
          <Grid item>
            <Grid container justifyContent="center">
              <ToggleAudioButton disabled={isReconnecting} />
              <ToggleVideoButton disabled={isReconnecting} />
              <ToggleCaptionsButton disabled={isReconnecting} />
              {!isSharingScreen && !isMobile && <ToggleScreenShareButton disabled={isReconnecting} />}
              {defaultconfig.DISABLE_CONVERSATIONS !== 'true' && <ToggleChatButton />}
              <Hidden smDown>
                <Menu />
              </Hidden>
            </Grid>
          </Grid>
          <Hidden smDown>
            <Grid style={{ flex: 1 }}>
              <Grid container justifyContent="flex-end">
                <EndCallButton />
              </Grid>
            </Grid>
          </Hidden>
        </Grid>
      </footer>

      <Dialog open={showExtendDialog && isRecruiter} onClose={() => setShowExtendDialog(false)}>
        <DialogTitle>Meeting ending soon</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The interview is ending in {minutesRemaining} minute{minutesRemaining === 1 ? '' : 's'}. Add 10 more minutes?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowExtendDialog(false)} color="default">
            No thanks
          </Button>
          <Button onClick={handleExtend} color="primary" variant="contained">
            Add 10 minutes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}